import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Divider,
  Card,
  CardContent,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  CircularProgress,
  Alert,
  Tooltip,
  Menu,
  MenuItem,
  Fade,
  Snackbar,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  Collapse,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Person as PersonIcon,
  Folder as FolderIcon,
  LocalHospital as HospitalIcon,
  Description as DescriptionIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon,
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  MedicalServices as MedicalIcon,
  FamilyRestroom as FamilyIcon,
  CalendarMonth as CalendarIcon,
  Psychology as PsychologyIcon,
  Mic as MicIcon,
  HeadsetMic as HeadsetMicIcon,
  Image as ImageIcon,
  Assignment as AssignmentIcon,
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon,
  ChevronRight as ChevronRightIcon,
  Phone as PhoneIcon,
  Schedule as ScheduleIcon,
  Clear as ClearIcon,
  SortByAlpha as SortByAlphaIcon,
  AccessTime as AccessTimeIcon,
  FiberNew as FiberNewIcon,
  Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AutoAwesome as AutoAwesomeIcon,
  PrintOutlined as PrintIcon,
  Bloodtype as BloodtypeIcon,
} from '@mui/icons-material';
import { auth } from '../../firebase';
import ConsultaForm from './ConsultaForm';
import ConsultaVoz from './ConsultaVoz';
import TranscripcionConsulta from './TranscripcionConsulta';
import AnalizadorImagenes from './AnalizadorImagenes';
import AntecedentesTab from './AntecedentesTab';
import ResumenTab from './ResumenTab';
import ListaPacientesView from './ListaPacientesView';
import DialogNuevoPaciente from './DialogNuevoPaciente';
import ConsultasTab from './ConsultasTab';
import ArchivosTab from './ArchivosTab';
import CuentaTab from './CuentaTab';
import DialogCobrarConsulta from './DialogCobrarConsulta';
import DialogNuevoPago from './DialogNuevoPago';
import AlertasBanner from './AlertasBanner';
import { exportHistoriaClinicaPDF } from './exportHistoriaClinicaPDF';
import AgendaCitas from './AgendaCitas';
import { NaraTypewriter, NaraTerminalCard } from './NaraTerminal';
import BriefingPacienteCard from './BriefingPacienteCard';
import { publishNaraContext, clearNaraContext } from '../naraChatContext';
import AskNara from '../AskNara';

const API_URL = '/api';

// Transicion para diálogos
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const EMRDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados principales
  const [pacientes, setPacientes] = useState([]);
  const [totalPacientes, setTotalPacientes] = useState(0);
  const [busqueda, setBusqueda] = useState('');
  const [filtroSegmento, setFiltroSegmento] = useState('todos'); // todos | recientes | sin_visita | nuevos | inactivos
  const [orden, setOrden] = useState('alfabetico'); // alfabetico | ultima_visita | reciente
  const [tabActual, setTabActual] = useState(0);

  // Estado para paciente seleccionado
  const [pacienteSeleccionado, setPacienteSeleccionado] = useState(null);
  const [expedienteActual, setExpedienteActual] = useState(null);
  const [consultasRecientes, setConsultasRecientes] = useState([]);
  const [consultasRefreshKey, setConsultasRefreshKey] = useState(0);

  // Diálogos
  const [dialogNuevoPaciente, setDialogNuevoPaciente] = useState(false);
  const [dialogNuevaConsulta, setDialogNuevaConsulta] = useState(false);
  const [dialogTranscripcion, setDialogTranscripcion] = useState(false);
  const [dialogAnalizadorImagenes, setDialogAnalizadorImagenes] = useState(false);
  const [dialogAgenda, setDialogAgenda] = useState(false);
  const [dialogConsultaVoz, setDialogConsultaVoz] = useState(false);
  const [datosVozParaConsulta, setDatosVozParaConsulta] = useState(null);
  const [naraBriefing, setNaraBriefing] = useState(null);
  const [loadingBriefing, setLoadingBriefing] = useState(false);
  const [briefingExpandido, setBriefingExpandido] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [citaIdParaConsulta, setCitaIdParaConsulta] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // Dialog que bloquea la apertura de consulta cuando el expediente no tiene antecedentes capturados
  const [dialogAntecedentesRequeridos, setDialogAntecedentesRequeridos] = useState(false);

  // Heurística: el expediente "tiene antecedentes" si AL MENOS UNO de los 3 grupos básicos
  // (heredo-familiares, patológicos, no patológicos) tiene contenido. Gineco-obstétricos solo
  // aplica para mujeres y se valida aparte.
  const expedienteTieneAntecedentes = useCallback((exp) => {
    if (!exp) return true; // Si no hay expediente, no bloqueamos (se debe abrir desde otro lado)
    const hasContent = (obj) => {
      if (!obj || typeof obj !== 'object') return false;
      const keys = Object.keys(obj);
      if (keys.length === 0) return false;
      return keys.some(k => {
        const v = obj[k];
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'string') return v.trim().length > 0;
        if (typeof v === 'object' && v !== null) return Object.keys(v).length > 0;
        if (typeof v === 'number') return true;
        if (typeof v === 'boolean') return v === true;
        return false;
      });
    };
    return hasContent(exp.antecedentes_heredo_familiares) ||
           hasContent(exp.antecedentes_personales_patologicos) ||
           hasContent(exp.antecedentes_personales_no_patologicos);
  }, []);

  // Re-fetch del expediente actual desde el backend (para refrescar antecedentes
  // recién guardados desde AntecedentesTab y que el banner/dialog reaccione).
  const refrescarExpedienteActual = useCallback(async () => {
    if (!pacienteSeleccionado?.id || !userId) return;
    try {
      const res = await fetch(`${API_URL}/emr/expedientes/paciente/${pacienteSeleccionado.id}`, {
        headers: { 'X-User-Id': userId },
      });
      if (res.ok) {
        const exp = await res.json();
        setExpedienteActual(exp);
      }
    } catch (_) { /* silencioso, no crítico */ }
  }, [pacienteSeleccionado, userId]);

  // Wrapper que valida antecedentes antes de abrir ConsultaForm.
  // Si faltan, muestra dialog bloqueante; si están OK, abre el form.
  // expedienteOverride: para casos donde el state aún no tiene el expediente cargado
  // (ej: justo después de seleccionar paciente desde query params).
  const intentarAbrirConsulta = useCallback((datosIniciales = null, expedienteOverride = null) => {
    const exp = expedienteOverride || expedienteActual;
    if (!expedienteTieneAntecedentes(exp)) {
      setDatosVozParaConsulta(datosIniciales); // guardar datos por si el doctor vuelve después
      setDialogAntecedentesRequeridos(true);
      return;
    }
    if (datosIniciales) setDatosVozParaConsulta(datosIniciales);
    setDialogNuevaConsulta(true);
  }, [expedienteActual, expedienteTieneAntecedentes]);

  // Configuración de documentos por país
  const documentosPorPais = {
    MX: { nombre: 'México', documento: 'CURP', placeholder: 'GARC850101HDFRRL09', maxLength: 18, pattern: '^[A-Z]{4}[0-9]{6}[HM][A-Z]{5}[0-9A-Z][0-9]$' },
    CO: { nombre: 'Colombia', documento: 'Cédula de Ciudadanía', placeholder: '1234567890', maxLength: 12, pattern: '^[0-9]{6,12}$' },
    US: { nombre: 'Estados Unidos', documento: 'SSN', placeholder: '123-45-6789', maxLength: 11, pattern: '^[0-9]{3}-[0-9]{2}-[0-9]{4}$' },
    ES: { nombre: 'España', documento: 'DNI/NIE', placeholder: '12345678A', maxLength: 9, pattern: '^[0-9XYZ][0-9]{7}[A-Z]$' },
    PE: { nombre: 'Perú', documento: 'DNI', placeholder: '12345678', maxLength: 8, pattern: '^[0-9]{8}$' },
    AR: { nombre: 'Argentina', documento: 'DNI', placeholder: '12345678', maxLength: 8, pattern: '^[0-9]{7,8}$' },
    CL: { nombre: 'Chile', documento: 'RUT', placeholder: '12345678-9', maxLength: 10, pattern: '^[0-9]{7,8}-[0-9Kk]$' },
    EC: { nombre: 'Ecuador', documento: 'Cédula', placeholder: '1234567890', maxLength: 10, pattern: '^[0-9]{10}$' },
    VE: { nombre: 'Venezuela', documento: 'Cédula', placeholder: 'V-12345678', maxLength: 10, pattern: '^[VEJPvejp]-[0-9]{7,8}$' },
    OTHER: { nombre: 'Otro', documento: 'Documento de Identidad', placeholder: '', maxLength: 20, pattern: '.*' }
  };

  // Ciudades más pobladas (LatAm + España). Autocomplete permite escribir otras.
  const ciudadesOpciones = [
    // Colombia
    'Bogotá, Colombia', 'Medellín, Colombia', 'Cali, Colombia', 'Barranquilla, Colombia',
    'Cartagena, Colombia', 'Cúcuta, Colombia', 'Bucaramanga, Colombia', 'Pereira, Colombia',
    'Santa Marta, Colombia', 'Ibagué, Colombia', 'Manizales, Colombia', 'Villavicencio, Colombia',
    'Neiva, Colombia', 'Pasto, Colombia', 'Montería, Colombia', 'Valledupar, Colombia',
    'Armenia, Colombia', 'Popayán, Colombia', 'Sincelejo, Colombia', 'Tunja, Colombia',
    // México
    'Ciudad de México, México', 'Guadalajara, México', 'Monterrey, México', 'Puebla, México',
    'Tijuana, México', 'León, México', 'Ciudad Juárez, México', 'Zapopan, México',
    'Mérida, México', 'Querétaro, México', 'Torreón, México', 'Acapulco, México',
    'Cancún, México', 'Veracruz, México', 'Aguascalientes, México', 'Oaxaca, México',
    'Saltillo, México', 'San Luis Potosí, México', 'Hermosillo, México', 'Culiacán, México',
    // Otros
    'Lima, Perú', 'Arequipa, Perú', 'Trujillo, Perú',
    'Buenos Aires, Argentina', 'Córdoba, Argentina', 'Rosario, Argentina',
    'Santiago, Chile', 'Valparaíso, Chile',
    'Quito, Ecuador', 'Guayaquil, Ecuador',
    'Caracas, Venezuela', 'Maracaibo, Venezuela',
    'Madrid, España', 'Barcelona, España', 'Valencia, España', 'Sevilla, España',
    'La Paz, Bolivia', 'Santa Cruz, Bolivia',
    'Asunción, Paraguay',
    'Montevideo, Uruguay',
    'San José, Costa Rica', 'Panamá, Panamá', 'Tegucigalpa, Honduras', 'Managua, Nicaragua',
    'San Salvador, El Salvador', 'Ciudad de Guatemala, Guatemala',
    'Santo Domingo, República Dominicana', 'La Habana, Cuba', 'San Juan, Puerto Rico',
  ];

  // Nacionalidades comunes (ordenadas por probabilidad de uso)
  const nacionalidadesOpciones = [
    'Colombiana', 'Mexicana', 'Estadounidense', 'Española', 'Peruana',
    'Argentina', 'Chilena', 'Ecuatoriana', 'Venezolana', 'Boliviana',
    'Paraguaya', 'Uruguaya', 'Costarricense', 'Panameña', 'Hondureña',
    'Nicaragüense', 'Salvadoreña', 'Guatemalteca', 'Dominicana', 'Cubana',
    'Puertorriqueña', 'Brasileña', 'Portuguesa', 'Francesa', 'Italiana',
    'Alemana', 'Británica', 'Canadiense', 'Haitiana', 'Jamaiquina',
    'China', 'Japonesa', 'Coreana', 'India', 'Filipina',
    'Otra',
  ];

  // Prefijos telefónicos internacionales (los más usados)
  const prefijosTelefono = [
    { codigo: '+57',  bandera: '🇨🇴', pais: 'Colombia' },
    { codigo: '+52',  bandera: '🇲🇽', pais: 'México' },
    { codigo: '+1',   bandera: '🇺🇸', pais: 'Estados Unidos / Canadá' },
    { codigo: '+34',  bandera: '🇪🇸', pais: 'España' },
    { codigo: '+51',  bandera: '🇵🇪', pais: 'Perú' },
    { codigo: '+54',  bandera: '🇦🇷', pais: 'Argentina' },
    { codigo: '+56',  bandera: '🇨🇱', pais: 'Chile' },
    { codigo: '+593', bandera: '🇪🇨', pais: 'Ecuador' },
    { codigo: '+58',  bandera: '🇻🇪', pais: 'Venezuela' },
    { codigo: '+591', bandera: '🇧🇴', pais: 'Bolivia' },
    { codigo: '+595', bandera: '🇵🇾', pais: 'Paraguay' },
    { codigo: '+598', bandera: '🇺🇾', pais: 'Uruguay' },
    { codigo: '+506', bandera: '🇨🇷', pais: 'Costa Rica' },
    { codigo: '+507', bandera: '🇵🇦', pais: 'Panamá' },
    { codigo: '+502', bandera: '🇬🇹', pais: 'Guatemala' },
    { codigo: '+503', bandera: '🇸🇻', pais: 'El Salvador' },
    { codigo: '+504', bandera: '🇭🇳', pais: 'Honduras' },
    { codigo: '+505', bandera: '🇳🇮', pais: 'Nicaragua' },
    { codigo: '+509', bandera: '🇭🇹', pais: 'Haití' },
    { codigo: '+1809', bandera: '🇩🇴', pais: 'República Dominicana' },
    { codigo: '+53',  bandera: '🇨🇺', pais: 'Cuba' },
    { codigo: '+1787', bandera: '🇵🇷', pais: 'Puerto Rico' },
    { codigo: '+55',  bandera: '🇧🇷', pais: 'Brasil' },
    { codigo: '+351', bandera: '🇵🇹', pais: 'Portugal' },
    { codigo: '+33',  bandera: '🇫🇷', pais: 'Francia' },
    { codigo: '+39',  bandera: '🇮🇹', pais: 'Italia' },
    { codigo: '+49',  bandera: '🇩🇪', pais: 'Alemania' },
    { codigo: '+44',  bandera: '🇬🇧', pais: 'Reino Unido' },
  ];

  // Religiones comunes. El campo permite también "Otra".
  const religionesOpciones = [
    'Católica',
    'Cristiana / Evangélica',
    'Testigos de Jehová',
    'Mormona (SUD)',
    'Ortodoxa',
    'Judía',
    'Musulmana',
    'Budista',
    'Hindú',
    'Espiritualista',
    'Indígena / Tradicional',
    'Agnóstica',
    'Atea',
    'Ninguna',
    'Prefiere no decir',
    'Otra',
  ];

  // Formulario nuevo paciente
  const [editandoPacienteId, setEditandoPacienteId] = useState(null);
  const [dialogAntecedentes, setDialogAntecedentes] = useState(false);
  const [tabExpediente, setTabExpediente] = useState(0); // 0=Resumen, 1=Antecedentes, 2=Historial clínico, 3=Archivos, 4=Cuenta
  const [cobrarConsulta, setCobrarConsulta] = useState(null);  // consulta recién creada para cobrar
  const [pagoCargo, setPagoCargo] = useState(null);            // cargo a pagar (encadenamiento)
  const [nuevoPaciente, setNuevoPaciente] = useState({
    nombre_completo: '',
    fecha_nacimiento: '',
    sexo: 'masculino',
    nacionalidad: '',
    estado_civil: '',
    escolaridad: '',
    ocupacion: '',
    religion: '',
    lugar_nacimiento: '',
    lugar_residencia: '',
    domicilio: '',
    pais: 'CO',
    documento_identidad: '',
    prefijo_telefono: '+57',
    telefono: '',
    correo_electronico: '',
    tipo_sangre: ''
  });

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Autenticación
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (u) => {
      if (u) {
        setUser(u);
        const storedId = localStorage.getItem('user_id');
        if (storedId) {
          setUserId(storedId);
        } else {
          setUserId(u.uid);
          try {
            const res = await fetch(`${API_URL}/users/by_uid/${u.uid}`);
            const data = await res.json();
            if (data?.id) {
              const uid = String(data.id);
              localStorage.setItem('user_id', uid);
              setUserId(uid);
            }
          } catch (err) {
            console.error('Error obteniendo usuario:', err);
          }
        }
      } else {
        navigate('/login');
      }
    });
    return () => { unsubscribe(); clearNaraContext(); };
  }, [navigate]);

  // Cargar pacientes
  const cargarPacientes = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (busqueda) params.append('buscar', busqueda);
      params.append('limit', '50');

      const res = await fetch(`${API_URL}/emr/pacientes?${params}`, {
        headers: { 'X-User-Id': userId }
      });

      if (!res.ok) {
        if (res.status === 403) {
          setError('No tienes acceso al módulo de Expediente Médico. Contacta al administrador.');
          return;
        }
        throw new Error('Error al cargar pacientes');
      }

      const data = await res.json();
      setPacientes(data.pacientes || []);
      setTotalPacientes(data.total || 0);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [userId, busqueda]);

  useEffect(() => {
    if (userId) {
      cargarPacientes();
    }
  }, [userId, cargarPacientes]);

  // Si llegamos con ?cita_id=...&paciente_telefono=... abrir flujo "Iniciar consulta"
  useEffect(() => {
    const citaIdParam = searchParams.get('cita_id');
    const telParam = searchParams.get('paciente_telefono');
    if (!citaIdParam || !telParam || !pacientes.length) return;

    const norm = (s) => (s || '').replace(/\D/g, '');
    const telBuscado = norm(telParam);
    // 1) Intentar primero con la lista paginada del doctor (caso normal y rápido)
    const pacienteEnLista = pacientes.find(p => norm(p.telefono).endsWith(telBuscado.slice(-8)) ||
                                                 telBuscado.endsWith(norm(p.telefono).slice(-8)));
    const seleccionarYAbrir = (paciente) => {
      setCitaIdParaConsulta(parseInt(citaIdParam, 10));
      handleSeleccionarPaciente(paciente).then((expCargado) => {
        intentarAbrirConsulta(null, expCargado);
      });
    };

    if (pacienteEnLista) {
      seleccionarYAbrir(pacienteEnLista);
    } else {
      // 2) Fallback: buscar en backend SIN filtro de médico tratante
      // (cubre el caso donde el paciente fue registrado por otro doctor de la misma clínica)
      fetch(`${API_URL}/emr/pacientes/buscar-por-telefono?telefono=${encodeURIComponent(telParam)}`, {
        headers: { 'X-User-Id': userId },
      })
        .then(r => r.ok ? r.json() : { pacientes: [] })
        .then(data => {
          const pacienteRemoto = (data.pacientes || [])[0];
          if (pacienteRemoto) {
            seleccionarYAbrir(pacienteRemoto);
          } else {
            setSnackbar({
              open: true,
              message: `No se encontró paciente con teléfono ${telParam}. Créalo y vuelve a la cita.`,
              severity: 'warning',
            });
          }
        })
        .catch(() => {
          setSnackbar({
            open: true,
            message: `No se pudo buscar el paciente. Intenta seleccionarlo manualmente.`,
            severity: 'warning',
          });
        });
    }
    // Limpiar query params para no re-disparar
    setSearchParams({}, { replace: true });
  }, [searchParams, pacientes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Exportar historia clínica completa en PDF
  const handleExportPDF = async () => {
    if (!pacienteSeleccionado || !expedienteActual) return;
    try {
      // Traer datos completos del expediente
      const res = await fetch(`${API_URL}/emr/expedientes/${expedienteActual.id}/completo`, {
        headers: { 'X-User-Id': userId },
      });
      const data = await res.json();
      await exportHistoriaClinicaPDF({
        paciente: pacienteSeleccionado,
        expediente: expedienteActual,
        consultas: data.consultas || [],
        recetas: data.recetas || [],
        estudios: data.estudios || [],
      });
      setSnackbar({ open: true, message: 'Historia clínica exportada', severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: `Error al exportar: ${e.message}`, severity: 'error' });
    }
  };

  // Abrir diálogo en modo EDICIÓN con datos pre-cargados del paciente
  const handleEditarPaciente = (paciente) => {
    // Separar teléfono en prefijo + número
    let prefijoTel = '+57';
    let numeroTel = paciente.telefono || '';
    if (numeroTel) {
      const m = numeroTel.match(/^(\+\d{1,4})\s*(.*)$/);
      if (m) {
        prefijoTel = m[1];
        numeroTel = m[2].replace(/\D/g, '');
      } else {
        numeroTel = numeroTel.replace(/\D/g, '');
      }
    }

    // Recuperar país y domicilio de direccion JSONB
    const dir = paciente.direccion || {};
    const paisGuardado = dir.pais || 'CO';
    const documentoGuardado = paciente.curp || dir.numero_documento || '';

    // Formatear fecha al formato yyyy-MM-dd (compatible con input type=date)
    let fechaStr = '';
    if (paciente.fecha_nacimiento) {
      fechaStr = String(paciente.fecha_nacimiento).slice(0, 10);
    }

    setEditandoPacienteId(paciente.id);
    setNuevoPaciente({
      nombre_completo: paciente.nombre_completo || '',
      fecha_nacimiento: fechaStr,
      sexo: paciente.sexo || 'masculino',
      nacionalidad: paciente.nacionalidad || '',
      estado_civil: paciente.estado_civil || '',
      escolaridad: paciente.escolaridad || '',
      ocupacion: paciente.ocupacion || '',
      religion: paciente.religion || '',
      lugar_nacimiento: paciente.lugar_nacimiento || '',
      lugar_residencia: paciente.lugar_residencia || '',
      domicilio: dir.domicilio || '',
      pais: paisGuardado,
      documento_identidad: documentoGuardado,
      prefijo_telefono: prefijoTel,
      telefono: numeroTel,
      correo_electronico: paciente.correo_electronico || '',
      tipo_sangre: paciente.tipo_sangre || '',
    });
    setDialogNuevoPaciente(true);
  };

  // Crear o actualizar paciente
  const handleCrearPaciente = async (datosForm = null) => {
    try {
      // Si recibimos datos del nuevo diálogo, los usamos. Sino, leemos del state legacy.
      const fuente = datosForm || nuevoPaciente;

      // Obtener empresa_id del usuario
      const userRes = await fetch(`${API_URL}/users/${userId}`, {
        headers: { 'X-User-Id': userId }
      });
      const userData = await userRes.json();

      // empresa_id está en userData.empresa.id (no en userData.company_id)
      const empresaId = userData.empresa?.id || userData.company_id;
      if (!empresaId) {
        throw new Error('No se pudo obtener el ID de la empresa del usuario');
      }

      // Preparar datos del paciente con el documento de identidad según el país
      const docConfig = documentosPorPais[fuente.pais] || documentosPorPais.OTHER;
      const pacienteData = {
        nombre_completo: fuente.nombre_completo,
        fecha_nacimiento: new Date(fuente.fecha_nacimiento).toISOString(),
        sexo: fuente.sexo,
        nacionalidad: fuente.nacionalidad || null,
        estado_civil: fuente.estado_civil || null,
        escolaridad: fuente.escolaridad || null,
        ocupacion: fuente.ocupacion || null,
        religion: fuente.religion || null,
        lugar_nacimiento: fuente.lugar_nacimiento || null,
        lugar_residencia: fuente.lugar_residencia || null,
        curp: fuente.pais === 'MX' ? fuente.documento_identidad : null,
        numero_seguro_social: null,
        tipo_sangre: fuente.tipo_sangre || null,
        telefono: fuente.telefono
          ? `${fuente.prefijo_telefono} ${fuente.telefono}`.trim()
          : null,
        correo_electronico: fuente.correo_electronico || null,
        empresa_id: String(empresaId),
        // Dirección (JSONB): país + documento de identidad + domicilio textual
        direccion: {
          pais: fuente.pais,
          pais_nombre: docConfig.nombre,
          tipo_documento: docConfig.documento,
          numero_documento: fuente.documento_identidad,
          domicilio: fuente.domicilio || null
        }
      };

      // Detectar modo edición o creación
      const esEdicion = !!editandoPacienteId;
      const url = esEdicion
        ? `${API_URL}/emr/pacientes/${editandoPacienteId}`
        : `${API_URL}/emr/pacientes`;
      const metodo = esEdicion ? 'PUT' : 'POST';

      // En edición, el backend usa PacienteUpdate y no requiere empresa_id
      const payload = esEdicion
        ? (() => {
            const { empresa_id: _omit, ...rest } = pacienteData;
            return rest;
          })()
        : pacienteData;

      const res = await fetch(url, {
        method: metodo,
        headers: {
          'Content-Type': 'application/json',
          'X-User-Id': userId
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errData = await res.json();
        let errorMsg = esEdicion ? 'Error al actualizar paciente' : 'Error al crear paciente';
        if (errData.detail) {
          if (Array.isArray(errData.detail)) {
            errorMsg = errData.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
          } else if (typeof errData.detail === 'string') {
            errorMsg = errData.detail;
          }
        }
        throw new Error(errorMsg);
      }

      const pacienteCreado = await res.json();
      setSnackbar({
        open: true,
        message: esEdicion ? 'Paciente actualizado correctamente' : 'Paciente creado correctamente',
        severity: 'success'
      });
      setDialogNuevoPaciente(false);
      setEditandoPacienteId(null);
      setNuevoPaciente({
        nombre_completo: '',
        fecha_nacimiento: '',
        sexo: 'masculino',
        nacionalidad: '',
        estado_civil: '',
        escolaridad: '',
        ocupacion: '',
        religion: '',
        lugar_nacimiento: '',
        lugar_residencia: '',
        domicilio: '',
        pais: 'CO',
        documento_identidad: '',
        prefijo_telefono: '+57',
        telefono: '',
        correo_electronico: '',
        tipo_sangre: ''
      });
      cargarPacientes();

      // Crear expediente automáticamente SOLO si es un paciente nuevo
      if (!esEdicion) {
        await fetch(`${API_URL}/emr/expedientes`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-User-Id': userId
          },
          body: JSON.stringify({
            paciente_id: pacienteCreado.id,
            empresa_id: String(empresaId)
          })
        });
      }

    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    }
  };

  // Seleccionar paciente
  const handleSeleccionarPaciente = async (paciente) => {
    setPacienteSeleccionado(paciente);
    setTabActual(1);
    setNaraBriefing(null);

    // Publish context to NaraChat
    publishNaraContext('paciente', {
      id: paciente.id,
      nombre: paciente.nombre_completo,
      edad: paciente.fecha_nacimiento ? `${Math.floor((Date.now() - new Date(paciente.fecha_nacimiento).getTime()) / 31557600000)} anos` : null,
      sexo: paciente.sexo,
      telefono: paciente.telefono,
      tipo_sangre: paciente.tipo_sangre,
    });

    let expedienteCargado = null;
    try {
      // Obtener expediente
      const expRes = await fetch(`${API_URL}/emr/expedientes/paciente/${paciente.id}`, {
        headers: { 'X-User-Id': userId }
      });

      if (expRes.ok) {
        const exp = await expRes.json();
        setExpedienteActual(exp);
        expedienteCargado = exp;

        // Obtener timeline
        const timeRes = await fetch(`${API_URL}/emr/expedientes/${exp.id}/timeline?limit=10`, {
          headers: { 'X-User-Id': userId }
        });
        if (timeRes.ok) {
          const timeData = await timeRes.json();
          setConsultasRecientes(timeData.timeline || []);
        }
      } else {
        setExpedienteActual(null);
        setConsultasRecientes([]);
      }

      // Cargar briefing NARA (en paralelo, no bloquea)
      setLoadingBriefing(true);
      fetch(`${API_URL}/emr/ia/briefing/${paciente.id}`, {
        headers: { 'X-User-Id': userId }
      }).then(res => res.ok ? res.json() : null)
        .then(data => { if (data) setNaraBriefing(data); })
        .catch(() => {})
        .finally(() => setLoadingBriefing(false));

    } catch (err) {
      console.error('Error:', err);
    }
    return expedienteCargado;
  };

  // Renderizar lista de pacientes
  const renderListaPacientes = () => <ListaPacientesView
    pacientes={pacientes}
    totalPacientes={totalPacientes}
    busqueda={busqueda}
    setBusqueda={setBusqueda}
    filtroSegmento={filtroSegmento}
    setFiltroSegmento={setFiltroSegmento}
    orden={orden}
    setOrden={setOrden}
    loading={loading}
    onNuevoPaciente={() => setDialogNuevoPaciente(true)}
    onSeleccionar={handleSeleccionarPaciente}
    onEditar={handleEditarPaciente}
    calcularEdad={calcularEdad}
  />;

  // Glassmorphism base para paneles
  const glassPanel = (gradient) => ({
    p: 3,
    borderRadius: 4,
    background: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(20px)',
    boxShadow: '0 4px 24px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.9)',
    border: '1px solid rgba(255, 255, 255, 0.5)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0, left: 0, right: 0,
      height: 3,
      background: gradient
    }
  });

  // Renderizar vista de expediente
  const renderExpediente = () => (
    <Box sx={{ position: 'relative', pb: 8 }}>
      {/* Patient Banner Sticky — compacto, se queda fijo al scrollear */}
      <Paper
        elevation={0}
        sx={{
          mb: 1.5,
          background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
          color: '#fff',
          borderRadius: 3,
          position: 'sticky',
          top: 8,
          zIndex: 100,
          boxShadow: '0 4px 20px rgba(30, 58, 95, 0.25)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          px: 2, py: 1.2,
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}>
          <IconButton
            onClick={() => { setTabActual(0); setPacienteSeleccionado(null); clearNaraContext(); }}
            sx={{ color: '#fff', p: 0.6 }}
            size="small"
          >
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
          <Avatar sx={{
            width: 40, height: 40, bgcolor: '#fbbf24', color: '#1e3a5f',
            fontSize: 16, fontWeight: 700,
            boxShadow: '0 2px 8px rgba(251, 191, 36, 0.4)',
            flexShrink: 0,
          }}>
            {pacienteSeleccionado?.nombre_completo?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography sx={{
                fontWeight: 700, fontSize: 15, lineHeight: 1.2,
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}>
                {pacienteSeleccionado?.nombre_completo}
              </Typography>
              {expedienteActual?.numero_expediente && (
                <Chip
                  label={expedienteActual.numero_expediente}
                  size="small"
                  sx={{ height: 18, fontSize: 10, bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600 }}
                />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap', mt: 0.2 }}>
              <Typography sx={{ fontSize: 11.5, opacity: 0.85 }}>
                {calcularEdad(pacienteSeleccionado?.fecha_nacimiento)} años · {pacienteSeleccionado?.sexo}
              </Typography>
              {pacienteSeleccionado?.tipo_sangre && (
                <Chip
                  icon={<BloodtypeIcon sx={{ fontSize: 12, color: '#fff !important' }} />}
                  label={pacienteSeleccionado.tipo_sangre}
                  size="small"
                  sx={{ height: 16, fontSize: 9.5, bgcolor: 'rgba(220, 38, 38, 0.85)', color: '#fff', fontWeight: 600, '& .MuiChip-icon': { ml: 0.4 } }}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={briefingExpandido ? 'Ocultar briefing IA' : 'Ver briefing IA'}>
              <IconButton
                onClick={() => setBriefingExpandido((v) => !v)}
                sx={{
                  color: '#fff',
                  bgcolor: briefingExpandido ? 'rgba(251, 191, 36, 0.25)' : 'rgba(255,255,255,0.1)',
                  borderRadius: 2,
                  px: 1, py: 0.4,
                  fontSize: 12,
                  '&:hover': { bgcolor: 'rgba(251, 191, 36, 0.35)' },
                }}
                size="small"
              >
                <AutoAwesomeIcon sx={{ fontSize: 16, mr: 0.5 }} />
                <Typography sx={{ fontSize: 11.5, fontWeight: 700 }}>Briefing</Typography>
                {briefingExpandido
                  ? <ExpandLessIcon sx={{ fontSize: 16, ml: 0.3 }} />
                  : <ExpandMoreIcon sx={{ fontSize: 16, ml: 0.3 }} />}
              </IconButton>
            </Tooltip>
            <AskNara
              question={`Dame un resumen completo de ${pacienteSeleccionado?.nombre_completo}`}
              label="Pregunta NARA"
              sx={{
                border: '1px solid rgba(255,255,255,0.25)',
                background: 'rgba(255,255,255,0.1)',
                color: '#fff',
                '&:hover': { background: 'rgba(16,185,129,0.3)' },
              }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Banner de alergias/alertas - SIEMPRE visible */}
      <AlertasBanner
        expediente={expedienteActual}
        ultimaConsulta={null}
      />

      {/* Briefing IA — colapsable, controlado desde el banner */}
      <Collapse in={briefingExpandido} timeout={220}>
        <BriefingPacienteCard
          briefing={naraBriefing}
          loading={loadingBriefing}
          onRegenerar={pacienteSeleccionado ? () => {
            setLoadingBriefing(true);
            fetch(`${API_URL}/emr/ia/briefing/${pacienteSeleccionado.id}`, {
              headers: { 'X-User-Id': userId }
            }).then(res => res.ok ? res.json() : null)
              .then(data => { if (data) setNaraBriefing(data); })
              .catch(() => {})
              .finally(() => setLoadingBriefing(false));
          } : null}
        />
      </Collapse>

      {/* Tabs principales del expediente */}
      <Paper sx={{ mb: 2, borderRadius: 3, overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0' }}>
          <Tabs
            value={tabExpediente}
            onChange={(_, v) => setTabExpediente(v)}
            sx={{
              '& .MuiTab-root': {
                fontWeight: 700, fontSize: 13, textTransform: 'none',
                minHeight: 48, color: '#64748b',
              },
              '& .Mui-selected': { color: '#4f46e5 !important' },
              '& .MuiTabs-indicator': { backgroundColor: '#4f46e5', height: 3 },
            }}
          >
            <Tab label="Resumen" icon={<AssessmentIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Antecedentes" icon={<DescriptionIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Consultas" icon={<TimelineIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Archivos" icon={<FolderIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
            <Tab label="Facturación" icon={<ReceiptIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
          </Tabs>
        </Box>

        <Box sx={{ p: 2.5, background: '#f8fafc', minHeight: 400 }}>
          {tabExpediente === 0 && pacienteSeleccionado && expedienteActual && (
            <ResumenTab
              paciente={pacienteSeleccionado}
              expediente={expedienteActual}
              userId={userId}
              onNuevaConsulta={() => intentarAbrirConsulta()}
              onImprimir={() => window.print()}
              onIrAntecedentes={() => setTabExpediente(1)}
            />
          )}
          {tabExpediente === 2 && expedienteActual && (
            <ConsultasTab
              expediente={expedienteActual}
              userId={userId}
              refreshKey={consultasRefreshKey}
              onNuevaNotaEvolucion={() => {
                intentarAbrirConsulta({ tipo_consulta: 'subsecuente' });
              }}
              onEditar={async (consulta) => {
                // Cargar órdenes y recetas asociadas a la consulta
                let ordenesExistentes = [];
                let medicamentosExistentes = [];
                try {
                  const [rOrdenes, rRecetas] = await Promise.all([
                    fetch(`${API_URL}/emr/consultas/${consulta.id}/ordenes`, { headers: { 'X-User-Id': userId } }),
                    fetch(`${API_URL}/emr/consultas/${consulta.id}/recetas`, { headers: { 'X-User-Id': userId } }),
                  ]);
                  if (rOrdenes.ok) {
                    const d = await rOrdenes.json();
                    ordenesExistentes = Array.isArray(d) ? d : (d.ordenes || []);
                  }
                  if (rRecetas.ok) {
                    const d = await rRecetas.json();
                    const recetasList = Array.isArray(d) ? d : (d.recetas || []);
                    // Combinar todos los medicamentos de todas las recetas — marcados como ya guardados
                    recetasList.forEach(rx => {
                      const meds = Array.isArray(rx.medicamentos) ? rx.medicamentos : [];
                      meds.forEach(m => medicamentosExistentes.push({ ...m, _existente: true, _receta_id: rx.id }));
                    });
                  }
                } catch (e) { console.error('[EDIT] Error cargando ordenes/recetas:', e); }

                // Edición de consulta existente: NO validamos antecedentes (es flujo retro)
                setDatosVozParaConsulta({
                  _editandoConsultaId: consulta.id,
                  motivo_consulta: consulta.motivo_consulta || '',
                  padecimiento_actual: consulta.padecimiento_actual || '',
                  tipo_consulta: consulta.tipo_consulta || 'primera_vez',
                  exploracion_fisica: consulta.exploracion_fisica || {},
                  signos_vitales: consulta.signos_vitales || {},
                  interrogatorio_aparatos_sistemas: consulta.interrogatorio_aparatos_sistemas || {},
                  evolucion_problemas: consulta.evolucion_problemas || {},
                  impresion_diagnostica: consulta.impresion_diagnostica || '',
                  estudios_solicitados: consulta.estudios_solicitados || '',
                  interconsultas_solicitadas: consulta.interconsultas_solicitadas || '',
                  plan_tratamiento: consulta.plan_tratamiento || '',
                  pronostico: consulta.pronostico || '',
                  indicaciones_paciente: consulta.indicaciones_paciente || '',
                  diagnosticos_existentes: (consulta.diagnosticos || []).map(d => ({
                    id: d.id,
                    codigo_cie10: d.codigo_cie10,
                    descripcion_cie10: d.descripcion_cie10,
                    tipo_diagnostico: d.tipo_diagnostico || 'principal',
                    certeza: d.certeza || 'presuntivo',
                    temporalidad: d.temporalidad || 'primera_vez',
                  })),
                  ordenes_existentes: ordenesExistentes,
                  medicamentos_existentes: medicamentosExistentes,
                });
                setDialogNuevaConsulta(true);
              }}
            />
          )}
          {tabExpediente === 1 && expedienteActual && pacienteSeleccionado && (
            <AntecedentesTab
              expedienteId={expedienteActual.id}
              paciente={pacienteSeleccionado}
              userId={userId}
              onAntecedentesActualizados={refrescarExpedienteActual}
            />
          )}
          {tabExpediente === 3 && expedienteActual && (
            <ArchivosTab expediente={expedienteActual} paciente={pacienteSeleccionado} userId={userId} />
          )}
          {tabExpediente === 4 && pacienteSeleccionado && expedienteActual && (
            <CuentaTab paciente={pacienteSeleccionado} expediente={expedienteActual} userId={userId} />
          )}
        </Box>
      </Paper>

      {/* SpeedDial FAB — acciones consolidadas (bottom-right) */}
      <SpeedDial
        ariaLabel="Acciones rápidas del expediente"
        icon={<SpeedDialIcon />}
        open={fabOpen}
        onOpen={() => setFabOpen(true)}
        onClose={() => setFabOpen(false)}
        sx={{
          position: 'fixed',
          bottom: 24, right: 24,
          '& .MuiFab-primary': {
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 8px 24px rgba(99, 102, 241, 0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
            },
          },
        }}
      >
        <SpeedDialAction
          icon={<AddIcon />}
          tooltipTitle="Nueva consulta"
          tooltipOpen
          onClick={() => { setFabOpen(false); intentarAbrirConsulta(); }}
          sx={{
            '& .MuiSpeedDialAction-staticTooltipLabel': { whiteSpace: 'nowrap', fontWeight: 600, fontSize: 12 },
            '& .MuiFab-root': { bgcolor: '#fbbf24', color: '#1e3a5f', '&:hover': { bgcolor: '#f59e0b' } },
          }}
        />
        <SpeedDialAction
          icon={<MicIcon />}
          tooltipTitle="Consulta por voz"
          tooltipOpen
          onClick={() => { setFabOpen(false); setDialogConsultaVoz(true); }}
          sx={{
            '& .MuiSpeedDialAction-staticTooltipLabel': { whiteSpace: 'nowrap', fontWeight: 600, fontSize: 12 },
            '& .MuiFab-root': { bgcolor: '#10b981', color: '#fff', '&:hover': { bgcolor: '#059669' } },
          }}
        />
        <SpeedDialAction
          icon={<PrintIcon />}
          tooltipTitle="Exportar PDF"
          tooltipOpen
          onClick={() => { setFabOpen(false); handleExportPDF(); }}
          sx={{
            '& .MuiSpeedDialAction-staticTooltipLabel': { whiteSpace: 'nowrap', fontWeight: 600, fontSize: 12 },
            '& .MuiFab-root': { bgcolor: '#0ea5e9', color: '#fff', '&:hover': { bgcolor: '#0284c7' } },
          }}
        />
        <SpeedDialAction
          icon={<AutoAwesomeIcon />}
          tooltipTitle={briefingExpandido ? 'Ocultar briefing IA' : 'Ver briefing IA'}
          tooltipOpen
          onClick={() => { setFabOpen(false); setBriefingExpandido((v) => !v); }}
          sx={{
            '& .MuiSpeedDialAction-staticTooltipLabel': { whiteSpace: 'nowrap', fontWeight: 600, fontSize: 12 },
            '& .MuiFab-root': { bgcolor: '#8b5cf6', color: '#fff', '&:hover': { bgcolor: '#7c3aed' } },
          }}
        />
      </SpeedDial>
    </Box>
  );

  // Calcular edad
  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return 0;
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const m = hoy.getMonth() - nacimiento.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    return edad;
  };

  // Si hay error de acceso
  if (error) {
    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <WarningIcon sx={{ fontSize: 80, color: '#f59e0b', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>Acceso Restringido</Typography>
        <Typography color="text.secondary" sx={{ mb: 3, textAlign: 'center', maxWidth: 400 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/dashboard')}>
          Volver al Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f1f5f9', p: { xs: 2, md: 4 } }}>
      {/* Header principal */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate('/dashboard')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <HospitalIcon sx={{ fontSize: 40, color: '#1e3a5f', mr: 2 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e3a5f' }}>
              Expediente Médico
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sistema de gestión de expedientes clínicos
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="contained"
            startIcon={<CalendarIcon />}
            onClick={() => setDialogAgenda(true)}
            sx={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: 2,
              fontWeight: 600,
              px: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              }
            }}
          >
            Agenda de Citas
          </Button>
          <Chip
            icon={<VerifiedIcon />}
            label="NOM-024 | HIPAA"
            color="success"
            variant="outlined"
          />
          <IconButton onClick={cargarPacientes}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Contenido según tab */}
      {tabActual === 0 && renderListaPacientes()}
      {tabActual === 1 && renderExpediente()}

      {/* Dialog Nuevo / Editar Paciente — Patrón "crear ahora, completar después" */}
      <DialogNuevoPaciente
        open={dialogNuevoPaciente}
        onClose={() => {
          setDialogNuevoPaciente(false);
          setEditandoPacienteId(null);
        }}
        userId={userId}
        modoEdicion={Boolean(editandoPacienteId)}
        datosIniciales={editandoPacienteId ? nuevoPaciente : null}
        documentosPorPais={documentosPorPais}
        ciudadesOpciones={ciudadesOpciones}
        onAbrirExistente={(p) => handleSeleccionarPaciente(p)}
        onGuardar={async (datosForm) => {
          await handleCrearPaciente(datosForm);
        }}
      />

      {/* Dialog Nueva Consulta */}
      <ConsultaForm
        open={dialogNuevaConsulta}
        onClose={() => {
          setDialogNuevaConsulta(false);
          setDatosVozParaConsulta(null);
          setCitaIdParaConsulta(null);
        }}
        expedienteId={expedienteActual?.id}
        paciente={pacienteSeleccionado}
        userId={userId}
        citaId={citaIdParaConsulta}
        datosIniciales={datosVozParaConsulta}
        onConsultaCreated={(consulta) => {
          console.log('[EMR] Consulta creada:', consulta);
          setSnackbar({
            open: true,
            message: `✅ Consulta #${consulta?.id || ''} guardada correctamente`,
            severity: 'success'
          });
          setDatosVozParaConsulta(null);
          setConsultasRefreshKey(k => k + 1);
          handleSeleccionarPaciente(pacienteSeleccionado);
          // Disparar modal de cobro si es una consulta NUEVA (no edición)
          if (consulta?.id && !consulta._edicion) {
            setCobrarConsulta(consulta);
          }
        }}
      />

      {/* Dialog Transcripción de Consulta */}
      <TranscripcionConsulta
        open={dialogTranscripcion}
        onClose={() => setDialogTranscripcion(false)}
        consultaId={null}
        paciente={pacienteSeleccionado}
        userId={userId}
        onTranscripcionComplete={(data) => {
          setSnackbar({ open: true, message: 'Transcripción completada', severity: 'success' });
          console.log('Transcripción:', data);
        }}
      />

      {/* Dialog Antecedentes */}
      <Dialog
        open={dialogAntecedentes}
        onClose={() => setDialogAntecedentes(false)}
        maxWidth="lg"
        fullWidth
        TransitionComponent={Transition}
        PaperProps={{
          sx: {
            borderRadius: 3,
            m: { xs: 1, sm: 2 },
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)',
            color: '#fff',
            py: 2, px: 3,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <DescriptionIcon sx={{ fontSize: 26, color: '#fbbf24' }} />
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
                Antecedentes
              </Typography>
              <Typography sx={{ fontSize: 11, opacity: 0.85 }}>
                {pacienteSeleccionado?.nombre_completo}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setDialogAntecedentes(false)} sx={{ color: '#fff' }}>
            <ArrowBackIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, background: '#f8fafc' }}>
          {expedienteActual && pacienteSeleccionado && (
            <AntecedentesTab
              expedienteId={expedienteActual.id}
              paciente={pacienteSeleccionado}
              userId={userId}
              onAntecedentesActualizados={refrescarExpedienteActual}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog Analizador de Imágenes */}
      <AnalizadorImagenes
        open={dialogAnalizadorImagenes}
        onClose={() => setDialogAnalizadorImagenes(false)}
        expedienteId={expedienteActual?.id}
        paciente={pacienteSeleccionado}
        userId={userId}
        onAnalisisComplete={(data) => {
          setSnackbar({ open: true, message: 'Análisis de imagen completado', severity: 'success' });
          console.log('Análisis:', data);
        }}
      />

      {/* Dialog Agenda de Citas */}
      <AgendaCitas
        open={dialogAgenda}
        onClose={() => setDialogAgenda(false)}
        userId={userId}
        onCitaSelected={(cita) => {
          // Opcional: buscar al paciente de la cita seleccionada
          setSnackbar({ open: true, message: `Cita seleccionada: ${cita.paciente_nombre}`, severity: 'info' });
        }}
      />

      {/* Dialog Cobrar consulta — aparece tras guardar una consulta nueva */}
      <DialogCobrarConsulta
        open={Boolean(cobrarConsulta)}
        onClose={() => setCobrarConsulta(null)}
        consulta={cobrarConsulta}
        paciente={pacienteSeleccionado}
        userId={userId}
        onCargoCreado={() => {
          setSnackbar({ open: true, message: 'Cargo registrado correctamente', severity: 'success' });
        }}
        onAbrirPago={(cargo) => {
          setCobrarConsulta(null);
          setPagoCargo(cargo);
        }}
      />

      {/* Dialog Pago — encadenado desde DialogCobrarConsulta o disparado manualmente */}
      <DialogNuevoPago
        open={Boolean(pagoCargo)}
        onClose={() => setPagoCargo(null)}
        paciente={pacienteSeleccionado}
        cargoPreseleccionado={pagoCargo}
        cargosPendientes={pagoCargo ? [pagoCargo] : []}
        userId={userId}
        onCreado={() => {
          setPagoCargo(null);
          setSnackbar({ open: true, message: 'Pago registrado correctamente', severity: 'success' });
        }}
      />


      {/* Dialog Consulta por Voz */}
      <ConsultaVoz
        open={dialogConsultaVoz}
        onClose={() => setDialogConsultaVoz(false)}
        paciente={pacienteSeleccionado}
        userId={userId}
        expedienteId={expedienteActual?.id}
        onDatosExtraidos={(datos) => {
          setDialogConsultaVoz(false);
          intentarAbrirConsulta(datos);
          setSnackbar({ open: true, message: 'Datos extraídos por IA. Revisa el formulario prellenado.', severity: 'success' });
        }}
      />

      {/* Dialog bloqueante: faltan antecedentes antes de consultar */}
      <Dialog
        open={dialogAntecedentesRequeridos}
        onClose={() => setDialogAntecedentesRequeridos(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ background: 'linear-gradient(135deg, #b45309 0%, #92400e 100%)', color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DescriptionIcon sx={{ color: '#fbbf24' }} />
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>Antecedentes requeridos</Typography>
            <Typography sx={{ fontSize: 11, opacity: 0.9 }}>Norma NOM-024-SSA3-2010 / Resolución 1995 de 1999</Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, background: '#fef3c7' }}>
          <Typography sx={{ fontSize: 14, color: '#1e293b', mb: 1.5 }}>
            Este paciente no tiene antecedentes capturados. Es obligatorio registrarlos antes de iniciar la consulta médica.
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: '#475569' }}>
            Captura al menos uno de los grupos: heredo-familiares, patológicos o no patológicos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, background: '#fef3c7', gap: 1 }}>
          <Button
            onClick={() => setDialogAntecedentesRequeridos(false)}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#475569' }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setDialogAntecedentesRequeridos(false);
              setTabExpediente(1); // Pestaña Antecedentes (orden actual: 0=Resumen, 1=Antecedentes)
            }}
            sx={{
              textTransform: 'none', fontWeight: 700,
              background: 'linear-gradient(135deg, #b45309 0%, #92400e 100%)',
              boxShadow: '0 4px 12px rgba(180,83,9,0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #92400e 0%, #78350f 100%)' },
            }}
          >
            Capturar antecedentes ahora
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.severity === 'error' ? 10000 : 6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          variant="filled"
          sx={{ borderRadius: 2, fontSize: 14, fontWeight: 600, boxShadow: 6 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EMRDashboard;
